import {
  createRazorpayOrderCallable,
  createRazorpayOrderHttp,
} from "./checkoutApi";

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.Razorpay) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Razorpay script failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(s);
  });
}

export function isHttpCheckoutMode() {
  return process.env.REACT_APP_USE_HTTP_CHECKOUT === "true";
}

/**
 * Opens Razorpay Checkout for a one-time e-card purchase (INR).
 * Prefers Firebase Callable + RTDB draft order (sadhuramedutech-style); optional HTTP fallback.
 */
export async function openRazorpayForProduct(product, { onPaid, onDismiss } = {}) {
  const finalRupee =
    product.Price - (product.Price * (product.Discount || 0)) / 100;
  const amountRupees = Math.max(1, Math.round(finalRupee * 100) / 100);

  await loadRazorpayScript();

  const httpCheckout = isHttpCheckoutMode();

  let orderId;
  let amount;
  let currency;
  let keyId;
  let internalOrderId;

  if (httpCheckout) {
    const amountPaise = Math.max(100, Math.round(finalRupee * 100));
    const res = await createRazorpayOrderHttp({
      amountPaise,
      receipt: `gift_${product.id}_${Date.now()}`,
      notes: {
        productId: String(product.id),
        title: String(product.Tittle || "").slice(0, 200),
      },
    });
    orderId = res.orderId;
    amount = res.amount;
    currency = res.currency;
    keyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
  } else {
    const res = await createRazorpayOrderCallable(product, amountRupees);
    orderId = res.orderId;
    amount = res.amount;
    currency = res.currency;
    keyId = res.keyId;
    internalOrderId = res.internalOrderId;
  }

  const key =
    keyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
  if (!key) {
    throw new Error(
      "Missing Razorpay key. For callables the function returns keyId; otherwise set REACT_APP_RAZORPAY_KEY_ID."
    );
  }

  return new Promise((resolve, reject) => {
    const options = {
      key,
      order_id: orderId,
      amount,
      currency: currency || "INR",
      name: process.env.REACT_APP_SITE_NAME || "GifteKart",
      description: product.Tittle || "E-card",
      theme: { color: "#7c3aed" },
      async handler(response) {
        try {
          const merged = { ...response, internalOrderId };
          if (onPaid) onPaid(merged);
          resolve(merged);
        } catch (e) {
          console.error("[Razorpay verify]", e);
          reject(e instanceof Error ? e : new Error(String(e?.message || e)));
        }
      },
      modal: {
        ondismiss() {
          if (onDismiss) onDismiss();
          resolve(null);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      reject(e);
    }
  });
}
